import { rocketMarkup } from '../rocketArt';
import { ball, coverBg, fade, gloss, mix, n, rng, shade, shadowInk, tint, type ArtPal } from './kit';

/**
 * Liftoff: a friendly rocket climbing along a rising curve. `w` = 640 (tile) or 1200 (hero); the curve starts
 * low on the left, the rocket sits in the right half, the left third stays calm for labels.
 */
function liftoffScene(P: ArtPal, p: string, w: number): string {
  const h = 400;
  const rx = w === 640 ? 452 : 880; // rocket centre
  const ry = w === 640 ? 150 : 132;
  const top = P.dark ? mix(P.bg, P.blue, 0.1) : tint(P.sky, 0.9);
  const mid = P.dark ? mix(P.panel, P.teal, 0.08) : tint(P.teal, 0.9);
  const bottom = P.dark ? P.bg : P.bg;
  const bg = coverBg(p, P, top, mid, bottom, [rx - 20, ry + 30], w, h);

  // curve: m(t) = e^(kt) look — a Bezier from bottom-left to the rocket's tail
  const x0 = w * 0.06;
  const y0 = h - 44;
  const ex = rx - 56;
  const ey = ry + 30;
  const curve = `M${n(x0)} ${y0}C${n(x0 + (ex - x0) * 0.55)} ${y0 - 4} ${n(x0 + (ex - x0) * 0.8)} ${n(y0 - (y0 - ey) * 0.25)} ${n(ex)} ${n(ey)}`;

  const grid: string[] = [];
  for (let x = 64; x < w; x += 64) grid.push(`M${x} 0V${h}`);
  for (let y = 56; y < h; y += 56) grid.push(`M0 ${y}H${w}`);

  // stars / dust (deterministic)
  const r = rng(w);
  const dots: string[] = [];
  for (let i = 0; i < (w === 640 ? 26 : 46); i++) {
    const x = w * 0.3 + r() * w * 0.7;
    const y = 16 + r() * (h * 0.62);
    const s = 0.8 + r() * 1.8;
    dots.push(`<circle cx="${n(x)}" cy="${n(y)}" r="${n(s)}" fill="${P.dark ? P.ink : P.blue}" fill-opacity="${n(0.15 + r() * 0.4)}"/>`);
  }

  // soft clouds low on the right (volume: gradient + top-left gloss)
  const cloud = (cx: number, cy: number, k: number) =>
    `<g transform="translate(${n(cx)} ${n(cy)}) scale(${k})">` +
    `<ellipse cx="0" cy="26" rx="70" ry="10" fill="url(#${p}sh)"/>` +
    `<path d="M-58 18C-66 18-70 6-60 0C-62-16-44-24-32-14C-26-32 0-36 10-18C22-28 44-22 44-6C58-6 64 10 54 18Z" fill="url(#${p}cl)"/>` +
    gloss(-22, -14, 16, 5, -20, P.dark ? 0.18 : 0.7) +
    `</g>`;
  const cloudC = P.dark ? mix(P.panel, P.ink, 0.16) : '#FFFFFF';

  // planet accent (hero only has room for two)
  const planet = (cx: number, cy: number, rr: number, c: string, id: string) =>
    `<circle cx="${cx}" cy="${cy}" r="${rr}" fill="url(#${p}${id})"/>` + gloss(cx - rr * 0.35, cy - rr * 0.4, rr * 0.32, rr * 0.16, -35, 0.45) +
    `<ellipse cx="${cx}" cy="${cy}" rx="${n(rr * 1.7)}" ry="${n(rr * 0.42)}" transform="rotate(-14 ${cx} ${cy})" fill="none" stroke="${tint(c, 0.25)}" stroke-width="${n(rr * 0.12)}" stroke-opacity=".8"/>`;

  const defs =
    `<defs>${bg.defs}` +
    `<linearGradient id="${p}ln" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${P.teal}" stop-opacity=".2"/><stop offset=".45" stop-color="${P.teal}"/><stop offset="1" stop-color="${tint(P.teal, 0.3)}"/></linearGradient>` +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.45 : 0.14) +
    `<linearGradient id="${p}cl" x1=".2" y1="0" x2=".4" y2="1"><stop offset="0" stop-color="${tint(cloudC, 0.3)}"/><stop offset=".5" stop-color="${cloudC}"/><stop offset=".85" stop-color="${shade(cloudC, P.dark ? 0.15 : 0.06)}"/><stop offset="1" stop-color="${shade(cloudC, P.dark ? 0.3 : 0.12)}"/></linearGradient>` +
    ball(`${p}pa`, P.purple, 0.4, 0.4) +
    ball(`${p}pb`, P.orange, 0.4, 0.4) +
    `</defs>`;

  const extras =
    w === 640
      ? planet(590, 58, 18, P.purple, 'pa')
      : planet(1110, 74, 30, P.purple, 'pa') + planet(560, 70, 14, P.orange, 'pb');

  return (
    defs +
    bg.body +
    `<path d="${grid.join('')}" stroke="${P.dark ? P.ink : P.ink}" stroke-opacity="${P.dark ? 0.05 : 0.06}" stroke-width="1"/>` +
    dots.join('') +
    extras +
    cloud(rx + 70, h - 58, 1.15) +
    cloud(rx - 150, h - 30, 0.8) +
    (w === 640 ? '' : cloud(rx + 250, h - 90, 0.7)) +
    `<path d="${curve}" fill="none" stroke="${P.teal}" stroke-opacity="${P.dark ? 0.1 : 0.12}" stroke-width="22" stroke-linecap="round"/>` +
    `<path d="${curve}" fill="none" stroke="url(#${p}ln)" stroke-width="5" stroke-linecap="round"/>` +
    `<circle cx="${n(x0)}" cy="${y0}" r="5" fill="${P.teal}" fill-opacity=".5"/>` +
    `<g transform="translate(${rx - 96} ${ry - 96}) rotate(-28 96 96) scale(1.5)">${rocketMarkup(P, `${p}rk`)}</g>`
  );
}

export const crashCover = (P: ArtPal, p: string) => liftoffScene(P, p, 640);
export const crashHeroCover = (P: ArtPal, p: string) => liftoffScene(P, p, 1200);
